// SDUI runtime: data-source engine + action runner. Framework-agnostic (no React).
import { evalExpr, resolveBinding } from "../model/expr";
import { Store } from "../model/store";
import type { ActionDef, ActionRef, DataSource, SDUIDocument, Step, Trigger } from "../model/types";
import type { Transport } from "./transport";

export interface RuntimeHooks {
  confirm?(message: string): boolean | Promise<boolean>;
  navigate?(to: string, params?: Record<string, unknown>): void;
  sign?(documentRef: unknown, assurance?: string): unknown | Promise<unknown>;
  toast?(message: unknown, level?: string): void;
  /** Save {@code content} to the user's device as {@code filename} (client-side file download). */
  download?(filename: string, content: unknown, contentType?: string): void;
  onError?(err: unknown): void;
}

export function normalizeAction(a: ActionDef): { steps: Step[]; confirm?: string } {
  return Array.isArray(a) ? { steps: a } : a;
}

export class DataEngine {
  private byId = new Map<string, DataSource>();
  private unsubs: Array<() => void> = [];
  private timers: ReturnType<typeof setInterval>[] = [];

  constructor(
    private doc: SDUIDocument,
    private store: Store,
    private transport: Transport,
    private appId: string,
    private hooks: RuntimeHooks = {},
  ) {
    for (const ds of doc.data ?? []) this.byId.set(ds.id, ds);
  }

  start(): void {
    for (const ds of this.doc.data ?? []) {
      const triggers: Trigger[] = ds.trigger ?? ["onLoad"];
      for (const t of triggers) {
        if (t === "onLoad") void this.run(ds.id);
        else if (t.startsWith("onChange:")) this.watch(t.slice("onChange:".length), ds.id);
        else if (t.startsWith("interval:")) {
          const ms = Number(t.slice("interval:".length));
          if (ms > 0) this.timers.push(setInterval(() => void this.run(ds.id), ms));
        }
      }
    }
  }

  private watch(path: string, dsId: string): void {
    let last = JSON.stringify(this.store.get(path));
    this.unsubs.push(
      this.store.subscribe(() => {
        const now = JSON.stringify(this.store.get(path));
        if (now !== last) { last = now; void this.run(dsId); }
      }),
    );
  }

  async run(id: string): Promise<void> {
    const ds = this.byId.get(id);
    if (!ds) return;
    try {
      const scope = this.store.snapshot();
      const params = resolveBinding(ds.params ?? {}, scope) as Record<string, unknown>;
      const body = ds.body !== undefined ? resolveBinding(ds.body, scope) : undefined;
      const resp = await this.transport.fetchData(this.appId, ds.endpoint, { method: ds.method, params, body });
      const value = ds.select ? evalExpr(ds.select, { ...scope, response: resp }) : resp;
      this.store.set(ds.into, value as unknown);
    } catch (err) {
      this.hooks.onError?.(err);
    }
  }

  dispose(): void {
    for (const u of this.unsubs) u();
    for (const t of this.timers) clearInterval(t);
    this.unsubs = [];
    this.timers = [];
  }
}

export class ActionRunner {
  constructor(
    private doc: SDUIDocument,
    private store: Store,
    private data: DataEngine,
    private transport: Transport,
    private appId: string,
    private hooks: RuntimeHooks = {},
  ) {}

  /** Run an action reference: a named action, a single step, or inline steps. */
  async run(ref: ActionRef, extraScope: Record<string, unknown> = {}): Promise<void> {
    const def = this.resolve(ref);
    if (!def) return;
    if (def.confirm) {
      const ok = await this.hooks.confirm?.(def.confirm);
      if (this.hooks.confirm && !ok) return;
    }
    for (const step of def.steps) await this.step(step, extraScope);
  }

  private resolve(ref: ActionRef): { steps: Step[]; confirm?: string } | undefined {
    if (typeof ref === "string") {
      const a = this.doc.actions?.[ref];
      return a ? normalizeAction(a) : undefined;
    }
    if (Array.isArray(ref)) return { steps: ref };
    return { steps: [ref] };
  }

  private async step(s: Step, extra: Record<string, unknown>): Promise<void> {
    const scope = { ...this.store.snapshot(), ...extra };
    const rb = (v: unknown) => resolveBinding(v, scope);
    switch (s.op) {
      case "set": this.store.set(String(s.path), rb(s.value)); break;
      case "merge": this.store.merge(String(s.path), rb(s.value) as Record<string, unknown>); break;
      case "append": this.store.append(String(s.path), rb(s.value)); break;
      case "toggle": this.store.toggle(String(s.path)); break;
      case "clear": this.store.clear(String(s.path)); break;
      case "increment": this.store.increment(String(s.path), Number(rb(s.by) ?? 1)); break;
      case "remove": {
        if (s.index !== undefined) this.store.removeAt(String(s.path), Number(rb(s.index)));
        else if (typeof s.where === "string") {
          const where = s.where;
          this.store.removeWhere(String(s.path), (item, index) => !!evalExpr(where, { ...scope, item, index }));
        }
        break;
      }
      case "submit": {
        try {
          if (s.optimistic !== undefined && s.into) this.store.set(String(s.into), rb(s.optimistic));
          const resp = await this.transport.submit(this.appId, String(s.endpoint), {
            method: s.method as string | undefined,
            body: rb(s.body),
          });
          if (s.into) this.store.set(String(s.into), resp);
          if (s.onSuccess) await this.run(s.onSuccess as ActionRef, { response: resp });
        } catch (err) {
          if (s.onError) await this.run(s.onError as ActionRef, { error: err });
          else this.hooks.onError?.(err);
        }
        break;
      }
      case "refetch": await this.data.run(String(s.data)); break;
      case "navigate": this.hooks.navigate?.(String(s.to), rb(s.params) as Record<string, unknown> | undefined); break;
      case "sign": {
        const r = await this.hooks.sign?.(rb(s.documentRef), s.assuranceRequired as string | undefined);
        if (s.into && r !== undefined) this.store.set(String(s.into), r);
        if (s.onSuccess && r !== undefined) await this.run(s.onSuccess as ActionRef, { response: r });
        break;
      }
      case "toast": this.hooks.toast?.(rb(s.message), s.level as string | undefined); break;
      case "download":
        this.hooks.download?.(String(rb(s.filename) ?? "download"), rb(s.content), s.contentType as string | undefined);
        break;
      default: throw new Error(`unknown step op '${s.op}'`);
    }
  }
}
